import { useAuth0 } from '@auth0/auth0-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { buildApiUrl } from '@/utils/apiBase'
import { startBootstrapSpan } from '../lib/bootstrapTelemetry'
import { emitMemberClientTelemetry, endpointKeyForPath, parseServerTiming, classifyMembersBottleneck } from '../lib/diagnosticTelemetry'

const ENV_API_AUDIENCE =
  (typeof import.meta !== 'undefined' &&
    import.meta.env &&
    (import.meta.env.VITE_AUTH0_API_AUDIENCE || import.meta.env.VITE_AUTH0_AUDIENCE)) ||
  (typeof process !== 'undefined' &&
    process.env &&
    (process.env.REACT_APP_AUTH0_API_AUDIENCE ||
      process.env.VITE_AUTH0_API_AUDIENCE ||
      process.env.VITE_AUTH0_AUDIENCE)) ||
  null

const tokenRequests = new WeakMap()
const inflightReads = new Map()
const authClientIds = new WeakMap()
let nextAuthClientId = 1
const TOKEN_READY_TIMEOUT_MS = 5000
const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

let authenticatedGeneration = 1

export function invalidateMemberApiSession() {
  authenticatedGeneration += 1
  inflightReads.forEach((entry) => entry.controller.abort(new DOMException('Member session ended', 'AbortError')))
  inflightReads.clear()
}

function authClientId(client) {
  if (!authClientIds.has(client)) authClientIds.set(client, nextAuthClientId++)
  return authClientIds.get(client)
}

function stableObject(value) {
  if (!value || typeof value !== 'object') return value
  if (Array.isArray(value)) return value.map(stableObject)
  return Object.fromEntries(Object.keys(value).sort().map(key => [key, stableObject(value[key])]))
}

export function authorizationContextKey(tokenOptions = {}, identity = {}) {
  return JSON.stringify(stableObject({
    subject: identity.subject || '',
    sessionKey: identity.sessionKey || '',
    retryNonce: identity.retryNonce || 0,
    authorizationParams: tokenOptions.authorizationParams || {},
  }))
}

function emitTokenPhase(phase, detail = {}) {
  emitMemberClientTelemetry({
    event: 'members_token_phase',
    phase,
    clubCode: detail.clubCode || '',
    errorCode: detail.errorCode || null,
    durationMs: detail.durationMs || 0,
  })
}

export function classifyTokenError(error) {
  const code = String(error?.code || error?.error || error?.message || '').toLowerCase()
  if (error?.name === 'TimeoutError' || code === 'timeout') return 'timeout'
  if (code.includes('login_required') || code.includes('consent_required') || code.includes('interaction_required')) return 'login_required'
  if (code.includes('missing_refresh_token') || code.includes('invalid_grant')) return 'missing_refresh_token'
  if (code.includes('organization') || code.includes('organisation')) return 'organisation_mismatch'
  return 'sdk_failure'
}

export async function stableAccessToken(getAccessTokenSilently, tokenOptions, identity = {}) {
  let requests = tokenRequests.get(getAccessTokenSilently)
  if (!requests) { requests = new Map(); tokenRequests.set(getAccessTokenSilently, requests) }
  const key = authorizationContextKey(tokenOptions, identity)
  let current = requests.get(key)
  if (!current) {
    let settled = false
    const started = performance.now()
    const sdkCall = Promise.resolve().then(() => {
      emitTokenPhase('token_cache_lookup_started', { clubCode: identity.clubCode })
      emitTokenPhase('refresh_token_exchange_started', { clubCode: identity.clubCode })
      return getAccessTokenSilently(tokenOptions)
    }).then((token) => {
      settled = true
      emitTokenPhase('sdk_token_call_settled', { clubCode: identity.clubCode, durationMs: performance.now() - started })
      emitTokenPhase('token_cache_lookup_completed', { clubCode: identity.clubCode })
      emitTokenPhase('refresh_token_exchange_completed', { clubCode: identity.clubCode })
      return token
    }, (error) => {
      settled = true
      emitTokenPhase('refresh_token_exchange_failed', { clubCode: identity.clubCode, errorCode: classifyTokenError(error), durationMs: performance.now() - started })
      throw error
    })
    const timeoutMs = Number(identity.tokenTimeoutMs || TOKEN_READY_TIMEOUT_MS)
    const timeout = new Promise((_, reject) => setTimeout(() => {
      if (!settled) {
        emitTokenPhase('sdk_token_call_abandoned', { clubCode: identity.clubCode, errorCode: 'timeout', durationMs: performance.now() - started })
        reject(Object.assign(new Error('timeout'), { name: 'TimeoutError', code: 'timeout' }))
      }
    }, timeoutMs))
    current = Promise.race([sdkCall, timeout]).finally(() => {
      requests.delete(key)
      if (!requests.size) tokenRequests.delete(getAccessTokenSilently)
    })
    requests.set(key, current)
  }
  return current
}

export function classifyMemberReadError(error) {
  if (error?.name === 'TimeoutError') return 'timeout'
  if (error?.name === 'AbortError') return 'abort'
  return 'network'
}

function resolveAudience(preferredAudience) {
  return preferredAudience || ENV_API_AUDIENCE || null
}

export async function memberPortalApiFetch(getAccessTokenSilently, path, options = {}, config = {}) {
  const requestGeneration = authenticatedGeneration
  const requestStarted = performance.now()
  if (typeof getAccessTokenSilently !== 'function') {
    throw new Error('member_portal_auth_unavailable')
  }

  const apiAudience = resolveAudience(config.audience)

  let token
  const tokenStarted = performance.now()
  try {
    const explicitAuthorizationParams =
      config && typeof config.tokenAuthorizationParams === 'object' && config.tokenAuthorizationParams
        ? config.tokenAuthorizationParams
        : null
    const tokenAuthorizationParams = explicitAuthorizationParams || (apiAudience ? { audience: apiAudience } : {})
    const tokenOptions =
      Object.keys(tokenAuthorizationParams).length > 0
        ? { authorizationParams: tokenAuthorizationParams }
        : {}

    const endTokenRequest = startBootstrapSpan('access_token_request', { category: 'member_api' })

    token = await stableAccessToken(getAccessTokenSilently, tokenOptions, config.identity)
    if (requestGeneration !== authenticatedGeneration) throw new DOMException('Member session ended', 'AbortError')
    endTokenRequest({ status: token ? 'success' : 'empty' })
  } catch (error) {
    console.warn('[member portal] getAccessTokenSilently failure', {
      errorName: error?.name || null,
    })
    throw error
  }

  const url = buildApiUrl(path)
  const headers = {
    ...(options.headers || {}),
    Authorization: `Bearer ${token}`,
  }

  const tokenMs = performance.now() - tokenStarted
  const fetchStarted = performance.now()
  try {
    const response = await fetch(url, { ...options, headers })
    if (requestGeneration !== authenticatedGeneration) throw new DOMException('Member session ended', 'AbortError')
    const fetchMs = performance.now() - fetchStarted
    const server = parseServerTiming(response.headers.get('Server-Timing')); const requestId = response.headers.get('X-Privana-Request-Id') || null
    const baseDetail = { event: 'members_client_response', routeKey: config.routeKey || endpointKeyForPath(path), endpointKey: endpointKeyForPath(path), clubCode: config.identity?.clubCode || '', status: response.status, totalMs: performance.now() - requestStarted, tokenMs, fetchMs, parseMs: 0, serverAuthMs: server.auth, serverTokenVerifyMs: server.tokenverify, serverOrgMs: server.org, serverMemberLinkMs: server.memberlink, serverTenantMs: server.tenant, serverPoolCreateMs: server.poolcreate, serverDbAcquireMs: server.dbacquire, serverPoolCloseMs: server.poolclose, transactionMs: server.transaction, serverSqlMs: server.sql, serverExternalMs: server.external, serverSerializeMs: server.serializeprep, retryCount: config.retryCount || 0, timeout: false, cacheState: config.cacheState || 'miss', requestId, errorCode: response.ok ? null : 'http_error', bottleneck: classifyMembersBottleneck({ authMs: server.auth, dbAcquireMs: server.dbacquire, sqlMs: server.sql, externalMs: server.external, serializeMs: server.serializeprep, fetchMs, serverTotalMs: Object.values(server).reduce((sum,value)=>sum+value,0) }) }
    emitMemberClientTelemetry(baseDetail)
    const originalJson = response.json.bind(response)
    response.json = async () => {
      const parseStarted = performance.now(); let payload
      try { payload = await originalJson(); return payload }
      finally {
        const parseMs = performance.now() - parseStarted
        const detail = { ...baseDetail, event: 'members_client_parse', totalMs: performance.now() - requestStarted, parseMs, errorCode: response.ok ? null : String(payload?.error || 'http_error') }
        emitMemberClientTelemetry(detail)
      }
    }
    return response
  } catch (error) {
    emitMemberClientTelemetry({ event: 'members_client_request', routeKey: config.routeKey || endpointKeyForPath(path), endpointKey: endpointKeyForPath(path), clubCode: config.identity?.clubCode || '', status: 0, totalMs: performance.now() - requestStarted, tokenMs, fetchMs: performance.now() - fetchStarted, parseMs: 0, retryCount: config.retryCount || 0, timeout: error?.name === 'TimeoutError', cacheState: 'miss', requestId: null, errorCode: classifyMemberReadError(error), bottleneck: 'unknown' })
    throw error
  }
}

function normalizedHeaders(headers = {}) {
  return [...new Headers(headers).entries()]
    .filter(([name]) => name.toLowerCase() !== 'authorization')
    .sort(([a], [b]) => a.localeCompare(b))
}

export function authenticatedReadKey(path, options = {}, config = {}) {
  const identity = config.identity || {}
  return JSON.stringify(stableObject({
    subject: identity.subject || '', clubCode: identity.clubCode || '', organization: identity.organization || '', sessionKey: identity.sessionKey || '',
    authClientId: config.authClientId || 0, audience: resolveAudience(config.audience) || '', scope: config.scope || '', authorizationParams: config.tokenAuthorizationParams || {}, method: 'GET', url: buildApiUrl(path), headers: normalizedHeaders(options.headers), credentials: options.credentials || '', mode: options.mode || '',
  }))
}

function subscribe(entry, signal) {
  const subscriber = {}
  entry.subscribers.add(subscriber)
  return new Promise((resolve, reject) => {
    let finished = false
    const cleanup = () => { signal?.removeEventListener?.('abort', onAbort); entry.subscribers.delete(subscriber) }
    const onAbort = () => {
      if (finished) return
      finished = true; cleanup()
      if (!entry.settled && entry.subscribers.size === 0) entry.controller.abort(new DOMException('All read subscribers cancelled', 'AbortError'))
      reject(signal?.reason instanceof Error ? signal.reason : new DOMException('Read subscriber cancelled', 'AbortError'))
    }
    if (signal?.aborted) return onAbort()
    signal?.addEventListener?.('abort', onAbort, { once: true })
    entry.promise.then(response => { if (!finished) { finished = true; cleanup(); resolve(response.clone()) } }, error => { if (!finished) { finished = true; cleanup(); reject(error) } })
  })
}

export async function boundedMemberPortalRead(getAccessTokenSilently, path, options = {}, config = {}) {
  const timeoutMs = config.timeoutMs || 3500
  const dedupeKey = authenticatedReadKey(path, options, { ...config, authClientId: authClientId(getAccessTokenSilently) })
  let entry = inflightReads.get(dedupeKey)
  if (entry) return subscribe(entry, options.signal)
  entry = { controller: new AbortController(), subscribers: new Set(), settled: false, promise: null }
  entry.promise = (async () => {
    for (let attempt = 0; attempt < 2; attempt += 1) {
      if (entry.controller.signal.aborted) throw entry.controller.signal.reason
      const controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(new DOMException('Member read timed out', 'TimeoutError')), timeoutMs)
      const relayAbort = () => controller.abort(entry.controller.signal.reason)
      entry.controller.signal.addEventListener('abort', relayAbort, { once: true })
      try {
        const sharedOptions = { ...options }
        delete sharedOptions.signal
        const response = await memberPortalApiFetch(getAccessTokenSilently, path, { ...sharedOptions, method: 'GET', signal: controller.signal }, config)
        if (response.status < 500 || attempt === 1) return response
      } catch (error) {
        const normalized = controller.signal.reason instanceof Error ? controller.signal.reason : error
        if (entry.controller.signal.aborted || attempt === 1) throw normalized
      } finally {
        clearTimeout(timeout)
        entry.controller.signal.removeEventListener('abort', relayAbort)
      }
      await sleep(200)
    }
    throw new Error('member_read_failed')
  })().finally(() => { entry.settled = true; if (inflightReads.get(dedupeKey) === entry) inflightReads.delete(dedupeKey) })
  inflightReads.set(dedupeKey, entry)
  return subscribe(entry, options.signal)
}

export function useMemberTokenReadiness({ audience, organization, clubCode, enabled = true, timeoutMs = TOKEN_READY_TIMEOUT_MS } = {}) {
  const { getAccessTokenSilently, isAuthenticated, isLoading, user } = useAuth0()
  const [retryNonce, setRetryNonce] = useState(0)
  const retry = useCallback(() => setRetryNonce((value) => value + 1), [])
  const identity = useMemo(() => ({ subject: user?.sub || '', organization: organization || user?.org_id || '', clubCode: clubCode || '', sessionKey: user?.sid || '', tokenTimeoutMs: timeoutMs, retryNonce }), [clubCode, organization, retryNonce, timeoutMs, user?.org_id, user?.sid, user?.sub])
  const authorizationParams = useMemo(() => ({ ...(resolveAudience(audience) ? { audience: resolveAudience(audience) } : {}), ...(identity.organization ? { organization: identity.organization } : {}) }), [audience, identity.organization])
  const attemptKey = useMemo(() => authorizationContextKey({ authorizationParams }, identity), [authorizationParams, identity])
  const [state, setState] = useState({ status: 'restoring', errorCode: null, attemptKey: '' })
  useEffect(() => {
    if (!enabled || isLoading) { setState(s => ({ ...s, status: 'restoring', errorCode: null, attemptKey })); return }
    if (!isAuthenticated) { setState({ status: 'reauthentication_required', errorCode: 'login_required', attemptKey }); return }
    if (!user?.sub || !identity.organization) { setState({ status: 'reauthentication_required', errorCode: user?.sub ? 'missing_organisation' : 'missing_subject', attemptKey }); return }
    let cancelled = false
    setState({ status: 'restoring', errorCode: null, attemptKey })
    stableAccessToken(getAccessTokenSilently, { authorizationParams }, identity).then(() => { if (!cancelled) setState({ status: 'ready', errorCode: null, attemptKey }) }, (error) => { if (!cancelled) { const errorCode = classifyTokenError(error); setState({ status: errorCode === 'timeout' ? 'timed_out' : errorCode === 'login_required' || errorCode === 'missing_refresh_token' || errorCode === 'organisation_mismatch' ? 'reauthentication_required' : 'failed', errorCode, attemptKey }) } })
    return () => { cancelled = true }
  }, [attemptKey, enabled, getAccessTokenSilently, isAuthenticated, isLoading, user?.sub])
  return { ...state, retry, getAccessTokenSilently, tokenAuthorizationParams: authorizationParams, identity }
}

export function useMemberPortalApi({ audience, organization, scope, tokenReadiness } = {}) {
  const auth0 = useAuth0()
  const { getAccessTokenSilently, user } = auth0
  const identity = useMemo(() => ({ subject: user?.sub || '', organization: organization || user?.org_id || '', sessionKey: user?.sid || '' }), [organization, user?.org_id, user?.sid, user?.sub])
  const tokenAuthorizationParams = useMemo(() => ({ ...(resolveAudience(audience) ? { audience: resolveAudience(audience) } : {}), ...(scope ? { scope } : {}), ...(identity.organization ? { organization: identity.organization } : {}) }), [audience, identity.organization, scope])

  const memberPortalApiFetchWithAuth = useCallback(
    (path, options = {}) => memberPortalApiFetch(getAccessTokenSilently, path, options, { audience, identity, scope, tokenAuthorizationParams }),
    [audience, getAccessTokenSilently, identity, scope, tokenAuthorizationParams]
  )

  const memberPortalApiGet = useCallback(
    (path, options = {}) => {
      if (tokenReadiness && tokenReadiness.status !== 'ready') return Promise.reject(Object.assign(new Error('member_token_not_ready'), { code: tokenReadiness.errorCode || tokenReadiness.status }))
      const clubCode = path.match(/^\/api\/members-portal\/([^/]+)/)?.[1] || ''
      return boundedMemberPortalRead(getAccessTokenSilently, path, options, { audience, scope, identity: { ...identity, clubCode: decodeURIComponent(clubCode) }, tokenAuthorizationParams })
    },
    [audience, getAccessTokenSilently, identity, scope, tokenAuthorizationParams, tokenReadiness?.errorCode, tokenReadiness?.status]
  )

  const memberPortalApiPost = useCallback(
    (path, body, options = {}) => {
      const isFormData = typeof FormData !== 'undefined' && body instanceof FormData
      const isBlob = typeof Blob !== 'undefined' && body instanceof Blob
      const headers = {
        ...options.headers,
        ...(!isFormData && !isBlob ? { 'Content-Type': 'application/json' } : {}),
      }

      return memberPortalApiFetchWithAuth(path, {
        ...options,
        method: 'POST',
        headers,
        ...(body != null ? { body: isFormData || isBlob ? body : JSON.stringify(body) } : {}),
      })
    },
    [memberPortalApiFetchWithAuth]
  )
  const memberPortalApiPostBinary = useCallback((path, blob, options = {}) => {
    if (typeof Blob === 'undefined' || !(blob instanceof Blob)) return Promise.reject(Object.assign(new Error('unsupported_audio'), { code: 'unsupported_audio' }))
    return memberPortalApiFetchWithAuth(path, {
      ...options,
      method: 'POST',
      headers: { ...options.headers, 'Content-Type': blob.type },
      body: blob,
    })
  }, [memberPortalApiFetchWithAuth])
  const memberPortalApiPatch = useCallback((path,body,options={})=>memberPortalApiFetchWithAuth(path,{...options,method:'PATCH',headers:{'Content-Type':'application/json',...(options.headers||{})},body:JSON.stringify(body)}),[memberPortalApiFetchWithAuth])

  const memberPortalApiDelete = useCallback(
    (path, options = {}) =>
      memberPortalApiFetchWithAuth(path, {
        ...options,
        method: 'DELETE',
      }),
    [memberPortalApiFetchWithAuth]
  )

  return useMemo(
    () => ({
      memberPortalApiFetch: memberPortalApiFetchWithAuth,
      memberPortalApiGet,
      memberPortalApiPost,
      memberPortalApiPostBinary,
      memberPortalApiPatch,
      memberPortalApiDelete,
    }),
    [memberPortalApiFetchWithAuth, memberPortalApiGet, memberPortalApiPost, memberPortalApiPostBinary, memberPortalApiPatch, memberPortalApiDelete]
  )
}
